/**
 * Product report static constants.
 *
 * These mirror the backend's AVAILABLE_TABLES and field definitions.
 * Available immediately (no API call needed for basic UI rendering).
 * The API can augment/override these if the backend is running.
 */
import {
  COMMON_FILTER_OPERATORS,
  BOOLEAN_OPTIONS,
  PLATFORM_OPTIONS_WITH_WEB,
  type FilterFieldMeta,
} from '@/lib/filters';

/** Available BigQuery tables — matches backend AVAILABLE_TABLES */
export const AVAILABLE_TABLES: Record<string, string> = {
  product_events_intraday: 'Product Events',
  purchase_events_intraday: 'Purchase Events',
  screen_view_events_intraday: 'Screen View Events',
  push_events_intraday: 'Push Events',
  web_to_app_intraday: 'Web to App Events',
};

/** Date presets */
export const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_14_days', label: 'Last 14 days' },
  { value: 'last_28_days', label: 'Last 28 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_year', label: 'This year' },
] as const;

/** Chart type options */
export const CHART_TYPE_OPTIONS = [
  { value: 'ts', label: 'Time Series' },
  { value: 'pivot', label: 'Pivot Table' },
] as const;

/** Time interval options */
export const INTERVAL_OPTIONS = [
  { value: 'h', label: 'Hourly' },
  { value: 'd', label: 'Daily' },
  { value: 'w', label: 'Weekly' },
  { value: 'm', label: 'Monthly' },
] as const;

/** Count method options */
export const COUNT_OPTIONS = [
  { value: 'all', label: 'All Events' },
  { value: 'distinct', label: 'Unique Users' },
] as const;

/** Calculation method options (for numeric metric aggregation) */
export const CALC_OPTIONS = [
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
] as const;

/** Standard row dimension options for pivot tables */
export const ROW_DIMENSION_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'event_name', label: 'Event Name' },
  { value: 'platform', label: 'Platform' },
  { value: 'app_version', label: 'App Version' },
  { value: 'country', label: 'Country' },
  { value: 'city', label: 'City' },
  { value: 'device_model', label: 'Device Model' },
  { value: 'os_version', label: 'OS Version' },
] as const;

/** Standard filter fields — matches Python's AVAILABLE_FILTERS exactly */
export const FILTER_FIELDS = [
  { value: 'device_id', label: 'device_id' },
  { value: 'app_version', label: 'app_version' },
  { value: 'platform', label: 'platform' },
  { value: 'country', label: 'country' },
  { value: 'is_premium', label: 'is_premium' },
  { value: 'is_web_to_app', label: 'is_web_to_app' },
  { value: 'campaign_name', label: 'campaign_name' },
  { value: 'traffic_source', label: 'traffic_source' },
  { value: 'clicked_push', label: 'clicked_push' },
  { value: 'clicked_push_id', label: 'clicked_push_id' },
  { value: 'purchase_structure', label: 'purchase_structure' },
  { value: 'app_open_count', label: 'app_open_count' },
  { value: 'retention_days', label: 'retention_days' },
] as const;

/** Filter comparison operators */
export const FILTER_OPERATORS = COMMON_FILTER_OPERATORS;

/** Filter field metadata */
export const FILTER_FIELD_META: FilterFieldMeta = {
  device_id:              { type: 'text', placeholder: 'Enter device ID' },
  app_version:            { type: 'text', placeholder: 'e.g., 1.2.3' },
  platform:               { type: 'dropdown', options: [...PLATFORM_OPTIONS_WITH_WEB] },
  country:                { type: 'text', placeholder: 'e.g., US or United States' },
  is_premium:             { type: 'dropdown', options: [...BOOLEAN_OPTIONS] },
  is_web_to_app:          { type: 'dropdown', options: [...BOOLEAN_OPTIONS] },
  campaign_name:          { type: 'text', placeholder: 'Enter campaign name' },
  traffic_source:         { type: 'text', placeholder: 'Enter traffic source' },
  clicked_push:           { type: 'text', placeholder: 'Enter push ID' },
  clicked_push_id:   { type: 'text', placeholder: 'Enter push variant' },
  purchase_structure:     { type: 'text', placeholder: 'Enter purchase structure' },
  app_open_count:         { type: 'text', placeholder: 'Enter app open count' },
  retention_days:         { type: 'text', placeholder: 'Enter retention days' },
};

/**
 * Convert the AVAILABLE_TABLES map to Select options.
 */
export function getTableOptions(): Array<{ value: string; label: string }> {
  return Object.entries(AVAILABLE_TABLES).map(([value, label]) => ({
    value,
    label,
  }));
}
